import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { isReadOnlyPreviewAllowed } from "../../config/env";
import type { AuthState, User } from "./auth.types";
import { AuthProvider } from "./auth.types";

const AUTH_STORAGE_KEY = "cms_portal_auth";
const MOCK_ACCESS_PREVIEW_KEY = "cms_portal_mock_access_preview";
/** Legacy boolean key from the old "Preview READ access" checkbox. */
const LEGACY_MOCK_READ_ONLY_KEY = "cms_portal_mock_read_only";

export type MockAccessPreviewLevel = AuthState["mockAccessPreview"];

const MOCK_ACCESS_PREVIEW_LEVELS: MockAccessPreviewLevel[] = [
	"READ",
	"READ_WRITE",
	"APPROVE",
];

function isMockAccessPreviewLevel(
	value: string | null,
): value is MockAccessPreviewLevel {
	return MOCK_ACCESS_PREVIEW_LEVELS.includes(value as MockAccessPreviewLevel);
}

function getMockAccessPreview(): MockAccessPreviewLevel {
	if (!isReadOnlyPreviewAllowed()) {
		try {
			localStorage.removeItem(MOCK_ACCESS_PREVIEW_KEY);
			localStorage.removeItem(LEGACY_MOCK_READ_ONLY_KEY);
		} catch {
			// ignore
		}
		return "READ_WRITE";
	}
	try {
		const stored = localStorage.getItem(MOCK_ACCESS_PREVIEW_KEY);
		if (isMockAccessPreviewLevel(stored)) return stored;

		// Migrate legacy checkbox: checked → READ, otherwise default READ_WRITE
		if (localStorage.getItem(LEGACY_MOCK_READ_ONLY_KEY) === "true") {
			localStorage.setItem(MOCK_ACCESS_PREVIEW_KEY, "READ");
			localStorage.removeItem(LEGACY_MOCK_READ_ONLY_KEY);
			return "READ";
		}
		localStorage.removeItem(LEGACY_MOCK_READ_ONLY_KEY);
		return "READ_WRITE";
	} catch {
		return "READ_WRITE";
	}
}

function defaultAuthState(): AuthState {
	return {
		accessToken: null,
		refreshToken: null,
		user: null,
		isAuthenticated: false,
		authProvider: null,
		mockAccessPreview: getMockAccessPreview(),
	};
}

function getPersistedAuth(): AuthState {
	try {
		const raw = localStorage.getItem(AUTH_STORAGE_KEY);
		if (!raw) return defaultAuthState();
		const parsed = JSON.parse(raw) as {
			accessToken?: string;
			refreshToken?: string;
			user?: User;
			authProvider?: AuthProvider;
		};
		if (!parsed.accessToken || !parsed.user?.id) return defaultAuthState();
		const authProvider =
			parsed.authProvider === "ms" || parsed.authProvider === AuthProvider.SAML
				? AuthProvider.SAML
				: parsed.authProvider === AuthProvider.CREDENTIALS
					? AuthProvider.CREDENTIALS
					: (parsed.authProvider ?? null);
		return {
			accessToken: parsed.accessToken,
			refreshToken: parsed.refreshToken || null,
			user: parsed.user,
			isAuthenticated: true,
			authProvider,
			mockAccessPreview: getMockAccessPreview(),
		};
	} catch {
		return defaultAuthState();
	}
}

const initialState: AuthState = getPersistedAuth();

const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setCredentials: (
			state,
			action: PayloadAction<{
				accessToken: string;
				refreshToken: string;
				user: User;
				authProvider: AuthProvider;
			}>,
		) => {
			state.accessToken = action.payload.accessToken;
			state.refreshToken = action.payload.refreshToken;
			state.user = action.payload.user;
			state.isAuthenticated = true;
			state.authProvider = action.payload.authProvider;
			persistAuth(state);
		},
		/** Soft-update profile / module access without changing tokens. */
		updateUser: (state, action: PayloadAction<User>) => {
			if (!state.isAuthenticated) return;
			state.user = action.payload;
			persistAuth(state);
		},
		logout: (state) => {
			state.accessToken = null;
			state.refreshToken = null;
			state.user = null;
			state.isAuthenticated = false;
			state.authProvider = null;
			try {
				localStorage.removeItem(AUTH_STORAGE_KEY);
			} catch {
				// ignore
			}
		},
		setMockAccessPreview: (
			state,
			action: PayloadAction<MockAccessPreviewLevel>,
		) => {
			if (!isReadOnlyPreviewAllowed()) {
				state.mockAccessPreview = "READ_WRITE";
				return;
			}
			const next = isMockAccessPreviewLevel(action.payload)
				? action.payload
				: "READ_WRITE";
			state.mockAccessPreview = next;
			try {
				localStorage.setItem(MOCK_ACCESS_PREVIEW_KEY, next);
				localStorage.removeItem(LEGACY_MOCK_READ_ONLY_KEY);
			} catch {
				// ignore
			}
		},
	},
});

function persistAuth(state: AuthState) {
	try {
		localStorage.setItem(
			AUTH_STORAGE_KEY,
			JSON.stringify({
				accessToken: state.accessToken,
				refreshToken: state.refreshToken,
				user: state.user,
				authProvider: state.authProvider,
			}),
		);
	} catch {
		// ignore storage errors
	}
}

export const { setCredentials, updateUser, logout, setMockAccessPreview } =
	authSlice.actions;
export default authSlice.reducer;
